import React, {useCallback} from 'react';
import {
   AutoSaveForm,
   FormApi,
   reactNodeIsOfType,
   SubmissionErrors
} from '@unifocus/react-components';
import {reduxPromiseListener, UniFocusActionWithPayload} from '../redux';
import {MakeAsyncFunction} from '../redux/MakeAsyncFunction';
import {PayloadAction} from '@reduxjs/toolkit';

export type ReduxFormConvertPayload<T, U> = (payload: T) => U;

/**
 * {@docCategory ReduxFormSubmitHandler}
 */
export type ReduxFormSubmitHandlerProps<T = ReduxFormPayload<Record<string, any>>, U = T> = {

   /**
    * Called when the form data needs to be saved.
    */
   submitConfig: ReduxFormSubmitConfig<T, U>;
};

/**
 * {@docCategory ReduxFormSubmitHandler}
 */
export interface ReduxAutoSaveFormPayload<T> {

   /**
    * The names of the fields being saved
    */
   fieldNames: Array<keyof T>,

   /**
    * The form values
    */
   values: T;
}

/**
 * {@docCategory ReduxFormSubmitHandler}
 */
export interface ReduxFormPayload<T> {
   /**
    * The form values
    */
   values: T;

   submitErrors?: any;
}

export type ReduxFormPayloadAction<T> = PayloadAction<ReduxFormPayload<T>>;

export type ReduxAutoSaveFormPayloadAction<T> = PayloadAction<ReduxAutoSaveFormPayload<T>>;

/**
 * {@docCategory ReduxFormSubmitHandler}
 */
export interface ReduxFormSubmitConfig<T, U = T> {

   /**
    * The type of action to dispatch to begin the form submission.  The payload is of type
    * ReduxFormPayload.
    */
   start: string;

   /**
    * The type of action that will cause the form submission to be resolved.  If there are
    * submission errors, set a submitErrors property in the payload or use the getPayload
    * configuration property to map a custom payload to return the submission errors.
    */
   resolve: string;

   /**
    * The type of action that will cause the form submission to be rejected.  In the case of
    * submission errors, one should resolve and not reject.  Rejection is reserved for
    * communications or server exceptions.
    */
   reject: string;

   /**
    * A function to set the payload for the submission.
    *
    * @defaultvalue (payload) =\> (\{ payload \})
    */
   setPayload?: ReduxFormConvertPayload<T, U>;

   /**
    * A function to get the payload out of the resolve action to pass to resolve the form submission
    * with.
    *
    * @defaultvalue payload =\> ((payload.submitErrors) ? payload.submitErrors : undefined);
    */
   getPayload?: ReduxFormConvertPayload<any, SubmissionErrors | void | undefined>;

   /**
    * A function to get the error out of the reject action to pass to reject the form submission
    * with.
    *
    * @defaultvalue (payload) =\> payload.
    */
   getError?: ReduxFormConvertPayload<any, any>;
}

export const ReduxFormSubmitHandler = <T, U = T>(props: React.PropsWithChildren<ReduxFormSubmitHandlerProps<T, U>>) => {

   const { submitConfig, children } = props;

   const getPayload = submitConfig.getPayload || defaultGetPayload;

   const asyncFunctionGetPayload = useCallback((action: UniFocusActionWithPayload<T>): SubmissionErrors | void | undefined => {

      return getPayload(action.payload);
   }, [getPayload]);

   const asyncFunctionSetPayload = useCallback((action: UniFocusActionWithPayload<any>,
                                                payload: any): UniFocusActionWithPayload<U> => {

      const payloadResult: U = submitConfig.setPayload ? submitConfig.setPayload(payload) : {} as U;

      return { type: action.type, payload: payloadResult };
   }, [submitConfig]);

   const asyncFunctionCallback = useCallback(asyncFunction => {
      const child = React.Children.only(children);
      if (React.isValidElement(child)) {
         if (reactNodeIsOfType(child, AutoSaveForm)) {
            return React.cloneElement(child, { key: 1, onSave: onSave(asyncFunction) } as any);
         }
         else {
            return React.cloneElement(child, { key: 1, onSubmit: onSubmit(asyncFunction) } as any);
         }
      }
      return <>{child}</>;
   }, [children]);

   return (
      <MakeAsyncFunction<ReduxFormPayload<T>, U>
         listener={reduxPromiseListener}
         start={submitConfig.start}
         resolve={submitConfig.resolve}
         reject={submitConfig.reject}
         setPayload={submitConfig.setPayload ? asyncFunctionSetPayload : undefined}
         getPayload={asyncFunctionGetPayload}
         getError={submitConfig.getError}
      >
         {asyncFunctionCallback}
      </MakeAsyncFunction>
   );
};

const defaultGetPayload = (payload: any): SubmissionErrors | void | undefined => {
   if (payload.submitErrors !== undefined) {
      return payload.submitErrors;
   }
   else {
      return undefined;
   }
};

const onSubmit = <T, >(asyncFunction: any) => (values: T, formApi: FormApi) => {
   return asyncFunction({
                           values: values,
                           formApi: formApi
                        });
};

const onSave = <T, >(asyncFunction: any) => (fieldNames: Array<keyof T>, values: T) => {
   return asyncFunction({
                           fieldNames: fieldNames,
                           values: values
                        });
};
